import { createContext, useContext } from 'react';
import AllApoHoloSiteDto from '../models/AllApoHoloSiteDto';
import { PocketResiduesResult } from '../models/PocketResiduesResult';
import { SelectStructureDto } from '../models/SelectStructureDto';
import { StructureSequenceDto } from '../models/StructureSequenceDto';

/**
 * Shared read data context: site lists, sequence maps, RCSB viewer selection, and
 * global representation colors. Unlike `StructureStateContext` (which tracks mutable
 * per-row UI state), `AhojDataContext` owns data that either comes from the backend
 * or represents the global default colors shown in `ControlPanel`.
 */
export interface AhojDataContextType {
   /** All site descriptors grouped by type, used to enumerate structures. */
   apoHoloSites: AllApoHoloSiteDto;
   /** Map from `row_id` to pocket residue auth_seq_ids, pre-fetched from the backend. */
   residueMapping: Map<string, PocketResiduesResult>;
   /** UniProt sequence cache keyed by UniProt accession. */
   uniprotSequenceMap: Map<string, StructureSequenceDto>;
   /** PDB FASTA sequence cache keyed by PDB code. */
   pdbSequenceMap: Map<string, StructureSequenceDto>;
   /** Currently selected UniProt entry for the RCSB sequence viewer base track. */
   selectedUniprot: SelectStructureDto | null;
   /** PDB structures currently shown as tracks in the RCSB sequence viewer. */
   selectedPdb: SelectStructureDto[];
   setSelectedPdb: (pdbs: SelectStructureDto[]) => void;
   setSelectedUniprot: (uniprot: SelectStructureDto | null) => void;
   /** Global default pocket color; propagated to all rows that have no custom color pair. */
   globalApoPocketColor: string;
   globalHoloPocketColor: string;
   globalPocketColor: string;
   globalAlpafoldPocketColor: string;

   globalApoRestColor: string;
   globalHoloRestColor: string;
   globalRestColor: string;
   globalAlpafoldRestColor: string;

   setGlobalApoPocketColor: (color: string) => void;
   setGlobalHoloPocketColor: (color: string) => void;
   setGlobalPocketColor: (color: string) => void;
   setGlobalAlpafoldPocketColor: (color: string) => void;

   setGlobalApoRestColor: (color: string) => void;
   setGlobalHoloRestColor: (color: string) => void;
   setGlobalRestColor: (color: string) => void;
   setGlobalAlpafoldRestColor: (color: string) => void;
}

export const AhojDataContext = createContext<AhojDataContextType>({} as AhojDataContextType);

/** Convenience hook for consuming `AhojDataContext` in function components. */
export const useAhojData = (): AhojDataContextType => useContext(AhojDataContext);
